import type { AnalyticsEvent, AnalyticsEventParamsOf } from "./events";

import * as Sentry from "@sentry/react";
import { isDevelopment, isUnitTest } from "@v2/utils/env";
import posthog from "posthog-js";
import {
  extractErrorTrackingEventDetails,
  shouldReportErrorToSentry,
} from "schemas/errors";

export const shouldLogAnalytics = () => {
  return (
    window.location.hostname !== "localhost" &&
    !window.location.hostname.includes("ngrok.app") &&
    !isDevelopment &&
    !isUnitTest
  );
};

export const trackError = (
  error: unknown,
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  extrasFromCaller?: Record<string, any>,
) => {
  const shouldExcludeErrorDueToUnitTesting = isUnitTest;
  const shouldExcludeErrorDueToAnalyticsConfig = !shouldLogAnalytics();
  const shouldExcludeErrorDueToStandardTracking =
    error instanceof Error && !shouldReportErrorToSentry(error);
  if (
    shouldExcludeErrorDueToUnitTesting ||
    shouldExcludeErrorDueToAnalyticsConfig ||
    shouldExcludeErrorDueToStandardTracking
  ) {
    // Bail out if we shouldn't track anything to Sentry
    return;
  }

  const trackingDetails = extractErrorTrackingEventDetails(error, undefined);
  Sentry.captureException(error, {
    captureContext: {
      fingerprint: trackingDetails?.fingerprint ?? ["{{ default }}"],
      extra: {
        url: window.location.href,
        ...extrasFromCaller,
        ...trackingDetails?.extra,
      },
      tags: trackingDetails?.tags ?? {},
    },
  });
};

export const analytics = {
  logEvent: <EventName extends AnalyticsEvent["type"]>(
    eventName: EventName,
    eventProperties: AnalyticsEventParamsOf<EventName>,
  ) => {
    Sentry.addBreadcrumb({
      category: "analyticsEvent",
      message: eventName,
      level: "info",
      data: eventProperties,
    });

    if (shouldLogAnalytics()) {
      void posthog?.capture(eventName, eventProperties);
    } else if (!isUnitTest) {
      console.log("Event:", eventName, eventProperties);
    }
  },
};
